// Orchestrates link-preview generation: fetch the page's metadata + image,
// render the card to a PNG, and stage it as a temp file ready for
// `addFileToDraft(path, name, 'File')` (attached as File so core sends the PNG
// uncompressed — see useLinkPreview; 'Image' would recode to alpha-less JPEG).
//
// NOTE ON SCOPE — this is a UI prototype, not the final design. Attaching the
// preview as an *image* is a deliberate compromise so we can build and validate
// the composer UX now while staying compatible with every existing client (an
// image needs no receive-side change). The intended end state is to move preview
// generation into *core* and carry the preview as real structured message
// metadata (title/description/image/host as first-class message fields), not a
// baked PNG. When that happens, this desktop card becomes a renderer over that
// metadata.
//
// Fetching is WEBAPP-ONLY and goes through the bridge's unfurl endpoint. We do
// NOT try a direct browser `fetch()` of the page first: browser fetches are
// CORS-bound and virtually every real site blocks cross-origin reads of its
// HTML, so a direct attempt almost always fails — and a blocked cross-origin
// GET still hits the site, so it just double-fetches (sender's IP + bridge).
// Instead we go straight to `GET /unfurl?url=…` on the configured ws-tcp
// bridge's own HTTP port (`packages/ws-tcp-proxy/unfurl.mjs`) — a hardened
// metadata fetcher (NOT a tunnel; that would be an open proxy) that fetches the
// page server-side and returns OpenGraph metadata + the image as JSON with
// permissive CORS. Nothing to configure app-side: we reuse the bridge URL. The
// endpoint is on by default for a local/allow-all bridge; if it's unreachable
// the composer shows a quiet "needs a bridge with unfurl" hint. (Native clients
// will fetch directly, no CORS, and need none of this.)

import { getLogger } from '@deltachat-desktop/shared/logger'
import { runtime } from '@deltachat-desktop/runtime-interface'

import { chooseLayout, type LinkMetadata, type PreviewLayout } from './openGraph'
import { renderCardPng, type Drawable } from './renderCard'

const log = getLogger('renderer/composer/linkPreview')

/**
 * Thrown when the unfurl endpoint can't be reached at all (no bridge, wrong
 * URL, connection refused, or the browser blocking a localhost fetch) — as
 * opposed to the endpoint responding that a given page has no preview. The
 * composer turns this into a "needs a bridge with unfurl enabled" hint rather
 * than silently doing nothing.
 */
export class UnfurlUnavailableError extends Error {}

/**
 * HTTP base of the unfurl endpoint, derived from the configured ws-tcp bridge:
 * the unfurl route lives on the bridge's own port, so there is nothing extra to
 * configure — we just reuse the bridge URL (`?proxy=` / `slothfulchat.proxyUrl`
 * / per-instance default, matching runtime.ts) and swap the scheme (ws→http,
 * wss→https). Webapp-only: native builds have no bridge URL.
 */
function unfurlBaseUrl(): string | null {
  try {
    const proxy =
      new URLSearchParams(window.location.search).get('proxy') ||
      window.localStorage.getItem('slothfulchat.proxyUrl') ||
      (window as any).__slothfulConfig?.defaultProxyUrl ||
      'ws://localhost:8641'
    if (!proxy || !/^wss?:\/\//.test(proxy)) {
      return null
    }
    return proxy.replace(/^ws(s?):\/\//, 'http$1://').replace(/\/+$/, '')
  } catch {
    return null
  }
}

/** Shape of the unfurl service's JSON reply (see packages/ws-tcp-proxy/unfurl.mjs). */
type UnfurlReply = {
  url: string
  title: string
  description: string | null
  isVideo: boolean
  twitterCard: string | null
  imageWidth: number | null
  imageHeight: number | null
  /** base64 (no data: prefix) og:image bytes, fetched by the service. */
  image: string | null
  imageMime: string | null
}

/**
 * Fetch `url` via the bridge's unfurl endpoint. Returns already-parsed metadata
 * plus the decoded thumbnail. Throws `UnfurlUnavailableError` when the endpoint
 * itself can't be reached (→ "needs a bridge with unfurl" hint), or a plain
 * Error when it responds but has no preview for this page (→ silent).
 */
async function fetchViaUnfurlService(
  url: string
): Promise<{ meta: LinkMetadata; thumbnail: Drawable }> {
  const base = unfurlBaseUrl()
  if (!base) {
    throw new UnfurlUnavailableError('no bridge configured')
  }
  let res: Response
  try {
    res = await fetch(`${base}/unfurl?url=${encodeURIComponent(url)}`)
  } catch (err) {
    // network-level failure: no bridge, refused, or the browser blocked the
    // localhost fetch (Local Network Access) — the endpoint is unreachable.
    throw new UnfurlUnavailableError(`unfurl endpoint unreachable: ${err}`)
  }
  if (!res.ok) {
    throw new Error(`unfurl replied ${res.status}`)
  }
  const data: UnfurlReply = await res.json()
  if (!data || typeof data.title !== 'string' || !data.title) {
    throw new Error('unfurl service returned no usable metadata')
  }

  let thumbnail: Drawable = null
  if (data.image) {
    try {
      const mime =
        data.imageMime && data.imageMime.startsWith('image')
          ? data.imageMime
          : 'image/jpeg'
      const img = await loadImage(`data:${mime};base64,${data.image}`)
      thumbnail = {
        image: img,
        naturalWidth: img.naturalWidth || 1,
        naturalHeight: img.naturalHeight || 1,
      }
    } catch (err) {
      log.info('unfurl thumbnail decode failed', url, err)
    }
  }

  const host = (() => {
    try {
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
    } catch {
      return url
    }
  })()
  const isVideo = !!data.isVideo
  const imageWidth = data.imageWidth ?? null
  const imageHeight = data.imageHeight ?? null
  const meta: LinkMetadata = {
    url,
    host,
    title: data.title,
    description: data.description ?? null,
    imageUrl: null, // the image came inline; nothing left to fetch
    imageWidth,
    imageHeight,
    isVideo,
    suggestedLayout: chooseLayout({
      isVideo,
      twitterCard: data.twitterCard ?? null,
      imageUrl: data.image ? url : null,
      imageWidth,
      imageHeight,
    }),
  }
  return { meta, thumbnail }
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = dataUrl
  })
}

/**
 * A prepared preview: the fetched metadata plus the decoded thumbnail, kept in
 * memory so the layout can be re-rendered (expand/collapse) without re-fetching.
 */
export type PreparedPreview = {
  meta: LinkMetadata
  thumbnail: Drawable
  /** Layout picked from metadata; may differ from the initial `suggestedLayout`
   *  once the real image aspect ratio is known. */
  initialLayout: PreviewLayout
}

/**
 * Fetch a URL into a ready-to-render preview, or `null` when the endpoint
 * responds but there's nothing to show. Throws `UnfurlUnavailableError` when
 * the unfurl endpoint can't be reached at all (the caller shows a hint).
 * `accountId` is unused now (kept for signature stability with the eventual
 * core-native path).
 */
export async function preparePreview(
  _accountId: number,
  url: string
): Promise<PreparedPreview | null> {
  let meta: LinkMetadata
  let thumbnail: Drawable
  try {
    ;({ meta, thumbnail } = await fetchViaUnfurlService(url))
  } catch (err) {
    if (err instanceof UnfurlUnavailableError) {
      throw err
    }
    log.info('no preview available for', url, err)
    return null
  }

  let initialLayout = meta.suggestedLayout
  // Refine when the site didn't declare dimensions but the decoded image is wide.
  if (
    initialLayout === 'compact' &&
    thumbnail &&
    !meta.isVideo &&
    thumbnail.naturalWidth / thumbnail.naturalHeight >= 1.6 &&
    thumbnail.naturalWidth >= 480
  ) {
    initialLayout = 'hero'
  }
  // A hero with no usable image looks empty — fall back to compact/text.
  if (initialLayout === 'hero' && !thumbnail) {
    initialLayout = 'compact'
  }
  return { meta, thumbnail, initialLayout }
}

export type StagedCard = {
  path: string
  fileName: string
  width: number
  height: number
  bytes: number
  /** The rendered PNG as a `data:` URL, so the composer can show the card
   *  (what will actually be sent) before the message goes out. */
  dataUrl: string
}

/** Render `prepared` at `layout` and stage a temp PNG (attached as File so it
 * ships uncompressed with its transparency intact). */
export async function stageCard(
  prepared: PreparedPreview,
  layout: PreviewLayout
): Promise<StagedCard> {
  const rendered = await renderCardPng({
    meta: prepared.meta,
    thumbnail: prepared.thumbnail,
    layout,
  })
  const fileName = 'link-preview.png'
  const path = await runtime.writeTempFileFromBase64(fileName, rendered.base64)
  return {
    path,
    fileName,
    width: rendered.width,
    height: rendered.height,
    bytes: rendered.bytes,
    dataUrl: `data:image/png;base64,${rendered.base64}`,
  }
}
